import React, { useState, useEffect, useRef } from "react";
import {
  // Navigation & Layout
  House,
  ChevronDown,
  Briefcase,

  // Master Module Icons
  Layers,
  Shield,
  UserCog,
  Building2,
  Briefcase as Department,
  Palette,
  FolderOpen,
  SquareMousePointer,
  Paintbrush,
  Clock,

  // Leads Module Icons
  FileQuestion,
  PhoneCall,
  MapPin,
  // FileText as Quotation,
  FileText,
  Clock as ClockIcon,
  Calculator as EstimateIcon,
  Ruler as SiteDesignerIcon,

  // HR Module Icons
  Users,
  UserCheck,
  CalendarDays,
  CalendarOff,
  Wallet,
  Leaf,
  CreditCard,
  IdCard,

  // Task Management
  ListTodo,

  // Clients
  Users2,

  // Projects
  FolderKanban,

  // Carpenter Module
  Hammer,
  Users as CarpenterIcon,
  Wrench,
  CheckCircle,

  // Inventory Module
  Boxes,
  Package,
  ShoppingBag,
  Archive,
  BarChart3,
  AlertTriangle,

  // Warehouse
  Warehouse,

  // Transport Module
  Truck,
  TruckIcon,
  UserCircle,
  MapPinned,
  PackageCheck,
  History,
  Radio,

  // Billing Module
  IndianRupee,
  Receipt,
  Plus,
  ClockArrowUp,
  FileSpreadsheet,
  RotateCcw,
  WalletCards,

  // Distributor & Trader Module Icons
  Store,
  Target,
  UsersRound,

  // Other Modules
  Megaphone,
  Video,
  Landmark,
  Activity,

  // Reports Module Icons
  LayoutDashboard as ReportsOverviewIcon,
  TrendingUp,
  ShoppingBag as PurchaseIcon,
  Package as StockIcon,
  FolderKanban as ProjectIcon,
  Users as LeadIcon,
  Store as DealerIcon,
  UserCheck as EmployeeIcon,
  Hammer as CarpenterReportIcon,
  Receipt as ExpenseIcon,
  AlertTriangle as DamageIcon,
  Truck as ShipmentIcon,
  Wallet as SalaryIcon,
  BarChart3 as BalanceSheetIcon,
  FileSpreadsheet as SalesLedgerIcon,
  CalendarDays as AttendanceReportIcon,
} from "lucide-react";

import { RiAdvertisementLine } from "react-icons/ri";
import "../Sidebar/Sidebar.css";
import Logo from "../../assests/image/Harwin_logo.png";
import { useNavigate, useLocation } from "react-router-dom";
import { usePageTitle } from "../../context/PageTitleContext";
import { usePermissions } from "../../context/PermissionContext";

// ─────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────

const RIcon = ({ icon: Icon, className = "", size = 16 }) => (
  <span
    className={`submenu-icon ${className}`}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: size,
      height: size,
      flexShrink: 0,
    }}
  >
    <Icon size={size} />
  </span>
);

const isReactIcon = (IconComponent) => {
  if (!IconComponent) return false;

  const name = IconComponent.displayName || IconComponent.name || "";

  return [
    "Ri",
    "Pi",
    "Fa",
    "Md",
    "Io",
    "Ai",
    "Bi",
    "Bs",
    "Ci",
    "Di",
    "Fi",
    "Gi",
    "Go",
    "Gr",
    "Hi",
    "Im",
    "Si",
    "Ti",
    "Tb",
    "Vsc",
    "Wi",
    "Cg",
    "Sl",
    "Rx",
  ].some((p) => name.startsWith(p));
};

// ─────────────────────────────────────────────
// Main Sidebar Component
// ─────────────────────────────────────────────

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { setTitle } = usePageTitle();

  const { canView, userRole } = usePermissions();

  // Determines whether Payslip routes to
  // admin approval queue or employee payslip page.
  const isHrAdmin =
    userRole === "Super Admin" ||
    userRole === "Branch Admin" ||
    userRole?.toLowerCase().includes("admin") ||
    userRole?.toLowerCase().includes("hr");

  // ───────────────────────────────────────────
  // State Variables
  // ───────────────────────────────────────────

  const [collapsed, setCollapsed] = useState(false);

  const [hoveredTooltip, setHoveredTooltip] = useState(null);

  const [tooltipPosition, setTooltipPosition] = useState({
    top: 0,
    left: 0,
  });

  const [hoveredSubmenuParent, setHoveredSubmenuParent] = useState(null);

  const [submenuTooltipPosition, setSubmenuTooltipPosition] = useState({
    top: 0,
    left: 0,
  });

  const leaveTimeoutRef = useRef(null);

  const [activeMainMenu, setActiveMainMenu] = useState(
    () => localStorage.getItem("activeMainMenu") || "dashboard",
  );

  const [activeSubMenu, setActiveSubMenu] = useState(
    () => localStorage.getItem("activeSubMenu") || null,
  );

  const [activeOpenMenu, setActiveOpenMenu] = useState(() => {
    const saved = localStorage.getItem("activeOpenMenu");

    return (
      saved ||
      ([
        "master",
        "leads",
        "hr",
        "inventory",
        "Craftsmen",
        "transport",
        "billing",
        "reports",
      ].includes(activeMainMenu)
        ? activeMainMenu
        : null)
    );
  });

  const isWarehousePage = location.pathname === "/warehouse";

  // ───────────────────────────────────────────
  // Effects
  // ───────────────────────────────────────────

  useEffect(() => {
    localStorage.setItem("activeMainMenu", activeMainMenu);

    localStorage.setItem("activeSubMenu", activeSubMenu || "");

    if (activeOpenMenu) {
      localStorage.setItem("activeOpenMenu", activeOpenMenu);
    } else {
      localStorage.removeItem("activeOpenMenu");
    }
  }, [activeMainMenu, activeSubMenu, activeOpenMenu]);

  // Auto-collapse: billing add/edit (as before) and the Reports page, so the
  // report table/graphs get the full width. Reports are still reachable from
  // the collapsed sidebar's hover flyout. If the sidebar was collapsed only
  // because of Reports, it re-opens when the user leaves Reports.
  const collapsedRef = useRef(collapsed);
  collapsedRef.current = collapsed;
  const autoCollapsedForReportsRef = useRef(false);

  useEffect(() => {
    const path = location.pathname;
    if (path.includes("/billing/add") || path.includes("/billing/edit")) {
      setCollapsed(true);
      return;
    }
    if (path === "/reports") {
      if (!collapsedRef.current) {
        autoCollapsedForReportsRef.current = true;
        setCollapsed(true);
      }
      return;
    }
    if (autoCollapsedForReportsRef.current) {
      autoCollapsedForReportsRef.current = false;
      setCollapsed(false);
    }
  }, [location.pathname]);

  // ───────────────────────────────────────────
  // Navigation Handlers
  // ───────────────────────────────────────────

  const handleMainClick = (
    menuKey,
    hasSubmenu = false,
    defaultPath = null,
    defaultTitle = null,
  ) => {
    setActiveMainMenu(menuKey);
    setActiveSubMenu(null);

    if (hasSubmenu) {
      setActiveOpenMenu(activeOpenMenu === menuKey ? null : menuKey);
    } else {
      setActiveOpenMenu(null);

      if (defaultPath) {
        navigate(defaultPath);

        let titleToSet = defaultTitle;

        if (!titleToSet) {
          titleToSet =
            menuKey === "billing"
              ? "Billing"
              : menuKey.charAt(0).toUpperCase() + menuKey.slice(1);
        }

        setTitle(titleToSet);
      }
    }
  };

  const goTo = (subKey, mainKey, path, title) => {
    setActiveSubMenu(subKey);
    setActiveMainMenu(mainKey);

    navigate(path);
    setTitle(title);

    if (window.innerWidth <= 768) {
      setActiveOpenMenu(null);
    }
  };

  const isOpen = (menuKey) => activeOpenMenu === menuKey;

  // ───────────────────────────────────────────
  // Tooltip Handlers
  // ───────────────────────────────────────────

  const handleMouseEnter = (e, text) => {
    if (collapsed) {
      const rect = e.currentTarget.getBoundingClientRect();

      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 10,
      });

      setHoveredTooltip(text);
    }
  };

  const handleMouseLeave = () => setHoveredTooltip(null);

  const handleSubmenuParentHover = (e, menuKey) => {
    if (collapsed) {
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);

        leaveTimeoutRef.current = null;
      }

      const rect = e.currentTarget.getBoundingClientRect();

      setSubmenuTooltipPosition({
        top: rect.top,
        left: rect.right + 10,
      });

      setHoveredSubmenuParent(menuKey);
    }
  };

  const handleSubmenuParentLeave = () => {
    leaveTimeoutRef.current = setTimeout(() => {
      setHoveredSubmenuParent(null);
    }, 200);
  };

  const handleSubmenuTooltipEnter = () => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);

      leaveTimeoutRef.current = null;
    }
  };

  const handleSubmenuTooltipLeave = () => {
    setHoveredSubmenuParent(null);
  };

  // ───────────────────────────────────────────
  // Reports Navigation
  // ───────────────────────────────────────────



  // ───────────────────────────────────────────
  // Submenu Data
  // ───────────────────────────────────────────

  const submenuData = {
    // ─────────────────────────────────────────
    // MASTER
    // ─────────────────────────────────────────

    master: [
      {
        key: "Roles",
        label: "Roles",
        icon: Shield,
        path: "/master/roles",
        canView: canView("users"),
      },
      {
        key: "users",
        label: "Users",
        icon: UserCog,
        path: "/master/users",
        canView: canView("users"),
      },
      {
        key: "branch",
        label: "Branch Master",
        icon: Building2,
        path: "/master/branch",
        canView: userRole === "Super Admin",
      },
      {
        key: "Department",
        label: "Department",
        icon: Department,
        path: "/master/department",
        canView: canView("department"),
      },
      {
        key: "colors",
        label: "Colors",
        icon: Palette,
        path: "/master/colors",
        canView: canView("colors"),
      },
      {
        key: "DocumentManager",
        label: "Document",
        icon: FolderOpen,
        path: "/master/documentmanager",
        canView: canView("documents"),
      },
      {
        key: "remainder",
        label: "Remainder",
        icon: Clock,
        path: "/master/reminders",
        canView: canView("remainder"),
      },
      {
        key: "shift",
        label: "Shift",
        icon: Clock,
        path: "/master/shift",
        canView: canView("shiftManagement"),
      },
    ],

    // ─────────────────────────────────────────
    // LEADS
    // ─────────────────────────────────────────

    leads: [
      {
        key: "enquiry-list",
        label: "Enquiry List",
        icon: FileText,
        path: "/leads/enquiry-list",
        canView: canView("leads") || canView("enquiryList"),
      },
      {
        key: "new-enquiry",
        label: "New Enquiry",
        icon: FileQuestion,
        path: "/leads/new-enquiry",
        canView: canView("leads") || canView("newEnquiry"),
      },
      {
        key: "follow-up",
        label: "Follow Ups",
        icon: PhoneCall,
        path: "/leads/follow-up",
        canView: canView("leads") || canView("followUps"),
      },
      {
        key: "site-visit",
        label: "Site Visit",
        icon: MapPin,
        path: "/leads/site-visit",
        canView: canView("leads") || canView("siteVisit"),
      },
      // {
      //   key: "quotation-sent",
      //   label: "Quotation Sent",
      //   icon: Quotation,
      //   path: "/leads/quotation-sent",
      //   canView: canView("leads") || canView("quotation"),
      // },
      {
        key: "future",
        label: "Future Follow",
        icon: ClockIcon,
        path: "/leads/future",
        canView: canView("leads") || canView("future"),
      },
      {
        key: "estimate",
        label: "Estimate",
        icon: EstimateIcon,
        path: "/leads/estimate",
        canView: canView("leads") || canView("estimate"),
      },
      {
        key: "site-designer",
        label: "Site Designer",
        icon: SiteDesignerIcon,
        path: "/leads/site-designer",
        canView: canView("leads") || canView("siteDesigner"),
      },
    ],

    // ─────────────────────────────────────────
    // HR
    // ─────────────────────────────────────────

    hr: [
      {
        key: "employee-master",
        label: "Employee",
        icon: UserCheck,
        path: "/hr/employee-master",
        canView: canView("employeeMaster"),
      },
      {
        key: "attendances",
        label: "Attendance",
        icon: CalendarDays,
        path: "/hr/attendances",
        canView: canView("attendances"),
      },
      {
        key: "holidays",
        label: "Holiday",
        icon: CalendarOff,
        path: "/hr/holidays",
        canView: canView("holidays"),
      },
      {
        key: "allowance",
        label: "Allowance",
        icon: Wallet,
        path: "/hr/allowance",
        canView: canView("travelAllowance"),
      },
      {
        key: "leave-management",
        label: "Leave",
        icon: Leaf,
        path: "/hr/leave-management",
        canView: canView("leaveManagement"),
      },
      {
        key: "salary-management",
        label: "Salary",
        icon: Wallet,
        path: "/hr/salary-management",
        canView: canView("salaryManagement"),
      },
      {
        key: "employee-advance",
        label: "Advance",
        icon: CreditCard,
        path: "/hr/employee-advance",
        canView: canView("employeeAdvance"),
      },

      isHrAdmin
        ? {
            key: "payslip-requests",
            label: "Payslip",
            icon: CreditCard,
            path: "/hr/paysliprequest",
            canView: canView("payslip"),
          }
        : {
            key: "employee-payslip",
            label: "My Payslip",
            icon: CreditCard,
            path: "/employeepayslip",
            canView: canView("payslip"),
          },

      {
        key: "id-card",
        label: "ID Card",
        icon: IdCard,
        path: "/hr/id-card",
        canView: canView("idCard"),
      },
    ],

    // ─────────────────────────────────────────
    // INVENTORY (Catalog added as first item)
    // ─────────────────────────────────────────

    inventory: [
      {
        key: "catalog",
        label: "Catalog",
        icon: FolderOpen,
        path: "/catalog",
        canView: canView("catalog"),
      },
      {
        key: "product",
        label: "Product",
        icon: Package,
        path: "/inventory/product",
        canView: canView("product"),
      },
      {
        key: "purchase",
        label: "Purchase",
        icon: ShoppingBag,
        path: "/inventory/purchase",
        canView: canView("purchase"),
      },
      {
        key: "purchase-return",
        label: "Purchase Return",
        icon: RotateCcw,
        path: "/inventory/purchase-return",
        canView: canView("purchase"),
      },
      {
        key: "stock",
        label: "Stock",
        icon: Archive,
        path: "/inventory/stock",
        canView: canView("stock"),
      },
      {
        key: "stock-report",
        label: "Stock Report",
        icon: BarChart3,
        path: "/inventory/stock-report",
        canView: canView("stock"),
      },
      {
        key: "damage",
        label: "Damage",
        icon: AlertTriangle,
        path: "/inventory/damage",
        canView: canView("damage"),
      },
    ],

    // ─────────────────────────────────────────
    // CRAFTSMEN
    // ─────────────────────────────────────────

    Craftsmen: [
      {
        key: "Craftsmen-master",
        label: "Carpenter",
        icon: CarpenterIcon,
        path: "/craftsmen/craftsmen-master",
        canView: canView("carpenterMaster"),
      },
      {
        key: "job-assigned",
        label: "Job Assigned",
        icon: Wrench,
        path: "/craftsmen/job-assigned",
        canView: canView("jobAssigned"),
      },
      {
        key: "work-completion",
        label: "Work Done",
        icon: CheckCircle,
        path: "/craftsmen/work-completion",
        canView: canView("workCompletion"),
      },
      {
        key: "carpenteradvance",
        label: "Advance",
        icon: CreditCard,
        path: "/craftsmen/carpenteradvance",
        canView: canView("carpenteradvance"),
      },
      {
        key: "agents",
        label: "Team",
        hidden: true,
        icon: Users,
        path: "/craftsmen/agents",
        canView: canView("agents"),
      },
    ],
    // ─────────────────────────────────────────
    // TRANSPORT
    // ─────────────────────────────────────────

    transport: [
      {
        key: "vehicles",
        label: "Vehicles",
        icon: TruckIcon,
        path: "/transport/vehicles",
        canView: canView("transport") || canView("vehicles"),
      },
      {
        key: "drivers",
        label: "Drivers",
        icon: UserCircle,
        path: "/transport/drivers",
        canView: canView("transport") || canView("drivers"),
      },
      {
        key: "shipment-history",
        label: "History",
        icon: History,
        path: "/transport/shipments/history",
        canView: canView("transport") || canView("shipmentHistory"),
      },
      {
        key: "transport-salary",
        label: "Salary",
        icon: Wallet,
        path: "/transport/drivers/salary",
        canView: canView("transport") || canView("transportSalary"),
      },
    ],

    // ─────────────────────────────────────────
    // BILLING
    // ─────────────────────────────────────────

    billing: [
      {
        key: "billing-list",
        label: "Billing List",
        icon: Receipt,
        path: "/billing",
        canView: canView("Billing"),
      },
      {
        key: "billing-add",
        label: "Add Billing",
        icon: Plus,
        path: "/billing/add",
        canView: canView("Billing"),
      },
      {
        key: "billing-pending",
        label: "Pending Billing",
        icon: ClockArrowUp,
        path: "/billing/pending-billing",
        canView: canView("Billing"),
      },
      {
        key: "balance-sheet",
        label: "Balance Sheet",
        icon: BarChart3,
        path: "/billing/balance-sheet",
        canView: canView("Billing"),
      },
      {
        key: "sales-ledger",
        label: "Ledger",
        icon: FileSpreadsheet,
        path: "/billing/sales-ledger",
        canView: canView("Billing"),
      },
      {
        key: "sale-return",
        label: "Sale Return",
        icon: RotateCcw,
        path: "/billing/sale-return",
        canView: canView("Billing"),
      },
    ],

    // ─────────────────────────────────────────
    // REPORTS
    // ─────────────────────────────────────────

    reports: [
      {
        key: "reports-overview",
        label: "Overview",
        icon: ReportsOverviewIcon,
        path: "/reports?tab=Overview",
        canView:
          canView("reportsSales") ||
          canView("reportsPurchase") ||
          canView("reportsStock") ||
          canView("reportsProducts") ||
          canView("reportsProject") ||
          canView("reportsLead") ||
          canView("reportsDealer") ||
          canView("reportsEmployee") ||
          canView("reportsAttendance") ||
          canView("reportsCarpenter") ||
          canView("reportsExpense") ||
          canView("reportsDamage") ||
          canView("reportsShipment") ||
          canView("reportsSalary") ||
          canView("reportsClients") ||
          canView("reportsTasks") ||
          canView("reportsPurchaseReturn") ||
          canView("reportsSaleReturn") ||
          canView("reportsLeave") ||
          canView("reportsAllowance") ||
          canView("reportsEmployeeAdvance") ||
          canView("reportsJobAssigned") ||
          canView("reportsWorkCompletion") ||
          canView("reportsVehicles") ||
          canView("reportsDrivers") ||
          canView("reportsDriverSalary") ||
          canView("reportsAssets") ||
          canView("reportsBalanceSheet") ||
          canView("reportsSalesLedger"),
      },
      {
        key: "reports-sales",
        label: "Sales",
        icon: TrendingUp,
        path: "/reports?tab=Sales",
        canView: canView("reportsSales"),
      },
      {
        key: "reports-purchase",
        label: "Purchase",
        icon: PurchaseIcon,
        path: "/reports?tab=Purchase",
        canView: canView("reportsPurchase"),
      },
      {
        key: "reports-stock",
        label: "Stock",
        icon: StockIcon,
        path: "/reports?tab=Stock",
        canView: canView("reportsStock"),
      },
      {
        key: "reports-products",
        label: "Products",
        icon: Boxes,
        path: "/reports?tab=Products",
        canView: canView("reportsProducts"),
      },
      {
        key: "reports-project",
        label: "Project",
        icon: ProjectIcon,
        path: "/reports?tab=Project",
        canView: canView("reportsProject"),
      },
      {
        key: "reports-lead",
        label: "Lead",
        icon: LeadIcon,
        path: "/reports?tab=Lead",
        canView: canView("reportsLead"),
      },
      {
        key: "reports-dealer",
        label: "Dealer",
        icon: DealerIcon,
        path: "/reports?tab=Dealer",
        canView: canView("reportsDealer"),
      },
      {
        key: "reports-employee",
        label: "Employee",
        icon: EmployeeIcon,
        path: "/reports?tab=Employee",
        canView: canView("reportsEmployee"),
      },
      {
        key: "reports-attendance",
        label: "Attendance",
        icon: AttendanceReportIcon,
        path: "/reports?tab=Attendance",
        canView: canView("reportsAttendance"),
      },
      {
        key: "reports-carpenter",
        label: "Carpenter",
        icon: CarpenterReportIcon,
        path: "/reports?tab=Carpenter",
        canView: canView("reportsCarpenter"),
      },
      {
        key: "reports-expense",
        label: "Expense",
        icon: ExpenseIcon,
        path: "/reports?tab=Expense",
        canView: canView("reportsExpense"),
      },
      {
        key: "reports-damage",
        label: "Damage",
        icon: DamageIcon,
        path: "/reports?tab=Damage",
        canView: canView("reportsDamage"),
      },
      {
        key: "reports-shipment",
        label: "Shipment",
        icon: ShipmentIcon,
        path: "/reports?tab=Shipment",
        canView: canView("reportsShipment"),
      },
      {
        key: "reports-salary",
        label: "Salary",
        icon: SalaryIcon,
        path: "/reports?tab=Salary",
        canView: canView("reportsSalary"),
      },
      {
        key: "reports-clients",
        label: "Clients",
        icon: Users2,
        path: "/reports?tab=Clients",
        canView: canView("reportsClients"),
      },
      {
        key: "reports-tasks",
        label: "Tasks",
        icon: ListTodo,
        path: "/reports?tab=Tasks",
        canView: canView("reportsTasks"),
      },
      {
        key: "reports-purchase-return",
        label: "Purchase Return",
        icon: RotateCcw,
        path: "/reports?tab=Purchase%20Return",
        canView: canView("reportsPurchaseReturn"),
      },
      {
        key: "reports-sale-return",
        label: "Sale Return",
        icon: RotateCcw,
        path: "/reports?tab=Sale%20Return",
        canView: canView("reportsSaleReturn"),
      },
      {
        key: "reports-leave",
        label: "Leave",
        icon: Leaf,
        path: "/reports?tab=Leave",
        canView: canView("reportsLeave"),
      },
      {
        key: "reports-allowance",
        label: "Allowance",
        icon: Wallet,
        path: "/reports?tab=Allowance",
        canView: canView("reportsAllowance"),
      },
      {
        key: "reports-employee-advance",
        label: "Employee Advance",
        icon: CreditCard,
        path: "/reports?tab=Employee%20Advance",
        canView: canView("reportsEmployeeAdvance"),
      },
      {
        key: "reports-job-assigned",
        label: "Job Assigned",
        icon: Wrench,
        path: "/reports?tab=Job%20Assigned",
        canView: canView("reportsJobAssigned"),
      },
      {
        key: "reports-work-completion",
        label: "Work Completion",
        icon: CheckCircle,
        path: "/reports?tab=Work%20Completion",
        canView: canView("reportsWorkCompletion"),
      },
      {
        key: "reports-vehicles",
        label: "Vehicles",
        icon: TruckIcon,
        path: "/reports?tab=Vehicles",
        canView: canView("reportsVehicles"),
      },
      {
        key: "reports-drivers",
        label: "Drivers",
        icon: UserCircle,
        path: "/reports?tab=Drivers",
        canView: canView("reportsDrivers"),
      },
      {
        key: "reports-driver-salary",
        label: "Driver Salary",
        icon: Wallet,
        path: "/reports?tab=Driver%20Salary",
        canView: canView("reportsDriverSalary"),
      },
      {
        key: "reports-assets",
        label: "Assets",
        icon: Landmark,
        path: "/reports?tab=Assets",
        canView: canView("reportsAssets"),
      },
      {
        key: "reports-balance-sheet",
        label: "Balance Sheet",
        icon: BalanceSheetIcon,
        path: "/reports?tab=Balance%20Sheet",
        canView: canView("reportsBalanceSheet"),
      },
      {
        key: "reports-sales-ledger",
        label: "Sales Ledger",
        icon: SalesLedgerIcon,
        path: "/reports?tab=Sales%20Ledger",
        canView: canView("reportsSalesLedger"),
      },
    ],
  };
  // Keep sidebar selection in sync with the current URL.
  // This makes browser Back/Forward, refreshes, direct URLs and nested
  // View/Edit/Detail/Track pages highlight the correct module automatically.
  useEffect(() => {
    const path = location.pathname.toLowerCase();

    const activateMain = (mainKey) => {
      setActiveMainMenu(mainKey);
      setActiveSubMenu(null);
      setActiveOpenMenu(null);
    };

    const activateSub = (mainKey, subKey) => {
      setActiveMainMenu(mainKey);
      setActiveSubMenu(subKey);
      setActiveOpenMenu(mainKey);
    };

    // Standalone modules first (some share a URL prefix with another module).
    if (path === "/leads/site-designer" || path.startsWith("/leads/site-designer/")) {
      activateMain("site-designer");
      return;
    }
    if (path === "/dashboard" || path === "/") {
      activateMain("dashboard");
      return;
    }
    if (path === "/clients" || path.startsWith("/clients/")) {
      activateMain("clients");
      return;
    }
    if (path === "/projects" || path.startsWith("/projects/")) {
      activateMain("projects");
      return;
    }
    if (path === "/warehouse" || path.startsWith("/warehouse/")) {
      activateMain("warehouse");
      return;
    }
    if (path === "/reports" || path.startsWith("/reports/")) {
      activateMain("reports");
      return;
    }

    // Master routes, including Brand which is rendered separately.
    if (path === "/master/brand" || path.startsWith("/master/brand/") ||
        path === "/brand/add" || path.startsWith("/brand/edit/")) {
      activateSub("master", "brand");
      return;
    }

    // Match submenu routes. Longest matching path wins, so nested pages such
    // as /edit/:id and /view/:id stay attached to their parent submenu.
    let bestMatch = null;
    Object.entries(submenuData).forEach(([mainKey, items]) => {
      (items || []).filter(Boolean).forEach((item) => {
        if (!item.path) return;
        const itemPath = item.path.split("?")[0].toLowerCase();
        if (path === itemPath || path.startsWith(`${itemPath}/`)) {
          if (!bestMatch || itemPath.length > bestMatch.itemPath.length) {
            bestMatch = { mainKey, subKey: item.key, itemPath };
          }
        }
      });
    });

    if (bestMatch) {
      activateSub(bestMatch.mainKey, bestMatch.subKey);
      return;
    }

    // Module fallbacks cover nested routes that are not represented directly
    // as a visible submenu item.
    if (path.startsWith("/master/")) return activateSub("master", null);
    if (path.startsWith("/hr/") || path.startsWith("/employeepayslip")) {
      setActiveMainMenu("hr");
      setActiveOpenMenu("hr");
      return;
    }
    if (path.startsWith("/leads/")) {
      setActiveMainMenu("leads");
      setActiveOpenMenu("leads");
      return;
    }
    if (path.startsWith("/craftsmen/")) {
      setActiveMainMenu("Craftsmen");
      setActiveOpenMenu("Craftsmen");
      return;
    }
    if (path.startsWith("/inventory/") || path.startsWith("/catalog")) {
      setActiveMainMenu("inventory");
      setActiveOpenMenu("inventory");
      return;
    }
    if (path.startsWith("/transport/")) {
      setActiveMainMenu("transport");
      setActiveOpenMenu("transport");
      return;
    }
    if (path === "/billing" || path.startsWith("/billing/")) {
      setActiveMainMenu("billing");
      setActiveOpenMenu("billing");
    }
  }, [location.pathname]);

  // Reports has no submenu in the sidebar any more (the report list lives
  // inside the Reports page) — just keep the menu item highlighted there.
  useEffect(() => {
    if (location.pathname === "/reports") {
      setActiveMainMenu("reports");
      setActiveSubMenu(null);
      setActiveOpenMenu((prev) => (prev === "reports" ? null : prev));
    }
  }, [location.pathname]);


  // ───────────────────────────────────────────
  // Sidebar Render
  // ───────────────────────────────────────────

  return (
    <>
      <aside
        className={`sidebar ${collapsed ? "collapsed" : ""} ${
          isWarehousePage ? "warehouse-sidebar" : ""
        }`}
      >
        {/* ─────────────────────────────────────
            SIDEBAR HEADER
            NOTE: matches CSS ".sidebar-top" /
            ".sidebar-logo-img" — the whole block
            is clickable and toggles collapse,
            exactly as ".sidebar-top { cursor:pointer }"
            in Sidebar.css implies.
        ───────────────────────────────────── */}

        <div
          className="sidebar-top"
          onClick={() => setCollapsed((prev) => !prev)}
          role="button"
          tabIndex={0}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <img src={Logo} alt="Harwin Logo" className="sidebar-logo-img" />
        </div>

        {/* ─────────────────────────────────────
            SIDEBAR BODY (SCROLLABLE)
            NOTE: renamed to "sidebar-scroll" to
            match Sidebar.css, which is what
            actually enables overflow-y: auto.
        ───────────────────────────────────── */}

        <div className="sidebar-scroll">
          <ul className="sidebar-menu">
            {/* ══════════════════════════════════
                MODULE 1: DASHBOARD
            ══════════════════════════════════ */}

            <li
              className={`menu-item ${
                activeMainMenu === "dashboard" ? "menu-active" : ""
              }`}
              onClick={() =>
                handleMainClick("dashboard", false, "/dashboard", "Dashboard")
              }
              onMouseEnter={(e) => handleMouseEnter(e, "Dashboard")}
              onMouseLeave={handleMouseLeave}
            >
              <House />

              {!collapsed && <span>Dashboard</span>}
            </li>

            {/* ══════════════════════════════════
                MODULE 2: MASTER
            ══════════════════════════════════ */}

            {(canView("users") ||
              userRole === "Super Admin" ||
              canView("brand") ||
              canView("department") ||
              canView("colors") ||
              canView("documents") ||
              canView("remainder") ||
              canView("shiftManagement")) && (
              <>
                <li
                  className={`menu-item ${
                    activeMainMenu === "master" ? "menu-active" : ""
                  }`}
                  onClick={() => handleMainClick("master", true)}
                  onMouseEnter={(e) => {
                    handleMouseEnter(e, "Master");

                    handleSubmenuParentHover(e, "master");
                  }}
                  onMouseLeave={() => {
                    handleMouseLeave();
                    handleSubmenuParentLeave();
                  }}
                >
                  <Layers />

                  {!collapsed && (
                    <>
                      <span>Master</span>

                      <ChevronDown
                        className={`dropdown-arrow ${
                          isOpen("master") ? "open" : ""
                        }`}
                      />
                    </>
                  )}
                </li>

                {isOpen("master") && !collapsed && (
                  <ul className="submenu">
                    {canView("users") && (
                      <li
                        className={
                          activeSubMenu === "Roles" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo("Roles", "master", "/master/roles", "Roles")
                        }
                      >
                        <Shield className="submenu-icon" />
                        <span>Roles</span>
                      </li>
                    )}

                    {canView("users") && (
                      <li
                        className={
                          activeSubMenu === "users" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo("users", "master", "/master/users", "Users")
                        }
                      >
                        <UserCog className="submenu-icon" />
                        <span>Users</span>
                      </li>
                    )}

                    {userRole === "Super Admin" && (
                      <li
                        className={
                          activeSubMenu === "branch" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "branch",
                            "master",
                            "/master/branch",
                            "Branch Master",
                          )
                        }
                      >
                        <Building2 className="submenu-icon" />
                        <span>Branch Master</span>
                      </li>
                    )}

                    {canView("brand") && (
                      <li
                        className={
                          activeSubMenu === "brand" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "brand",
                            "master",
                            "/master/brand",
                            "Brand Master",
                          )
                        }
                      >
                        <SquareMousePointer className="submenu-icon" />
                        <span>Brand Master</span>
                      </li>
                    )}

                    {canView("department") && (
                      <li
                        className={
                          activeSubMenu === "Department" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "Department",
                            "master",
                            "/master/department",
                            "Department",
                          )
                        }
                      >
                        <Department className="submenu-icon" />
                        <span>Department</span>
                      </li>
                    )}

                    {canView("colors") && (
                      <li
                        className={
                          activeSubMenu === "colors" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "colors",
                            "master",
                            "/master/colors",
                            "Colour Catalogue",
                          )
                        }
                      >
                        <Paintbrush className="submenu-icon" />
                        <span>Colors</span>
                      </li>
                    )}

                    {canView("documents") && (
                      <li
                        className={
                          activeSubMenu === "DocumentManager"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "DocumentManager",
                            "master",
                            "/master/documentmanager",
                            "Document",
                          )
                        }
                      >
                        <FolderOpen className="submenu-icon" />
                        <span>Documents</span>
                      </li>
                    )}

                    {canView("remainder") && (
                      <li
                        className={
                          activeSubMenu === "remainder" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "remainder",
                            "master",
                            "/master/reminders",
                            "Remainder",
                          )
                        }
                      >
                        <Clock className="submenu-icon" />
                        <span>Remainder</span>
                      </li>
                    )}

                    {canView("shiftManagement") && (
                      <li
                        className={
                          activeSubMenu === "shift" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo("shift", "master", "/master/shift", "shift")
                        }
                      >
                        <Clock className="submenu-icon" />
                        <span>Shift</span>
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}

            {/* ══════════════════════════════════
            {/* MODULE 4: HR MANAGEMENT */}
            {/* ════════════════════════════════════════════════════ */}

            {(canView("employeeMaster") ||
              canView("attendances") ||
              canView("holidays") ||
              canView("travelAllowance") ||
              canView("leaveManagement") ||
              canView("salaryManagement") ||
              canView("payslip") ||
              canView("employeeAdvance") ||
              canView("idCard")) && (
              <>
                <li
                  className={`menu-item ${
                    activeMainMenu === "hr" ? "menu-active" : ""
                  }`}
                  onClick={() => handleMainClick("hr", true)}
                  onMouseEnter={(e) => {
                    handleMouseEnter(e, "HR Management");
                    handleSubmenuParentHover(e, "hr");
                  }}
                  onMouseLeave={() => {
                    handleMouseLeave();
                    handleSubmenuParentLeave();
                  }}
                >
                  <Users />

                  {!collapsed && (
                    <>
                      <span>HR Management</span>
                      <ChevronDown
                        className={`dropdown-arrow ${
                          isOpen("hr") ? "open" : ""
                        }`}
                      />
                    </>
                  )}
                </li>

                {isOpen("hr") && !collapsed && (
                  <ul className="submenu">
                    {canView("employeeMaster") && (
                      <li
                        className={
                          activeSubMenu === "employee-master"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "employee-master",
                            "hr",
                            "/hr/employee-master",
                            "Employee Master",
                          )
                        }
                      >
                        <UserCheck className="submenu-icon" />
                        <span>Employee</span>
                      </li>
                    )}

                    {canView("attendances") && (
                      <li
                        className={
                          activeSubMenu === "attendances"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "attendances",
                            "hr",
                            "/hr/attendances",
                            "Attendances",
                          )
                        }
                      >
                        <CalendarDays className="submenu-icon" />
                        <span>Attendance</span>
                      </li>
                    )}

                    {canView("holidays") && (
                      <li
                        className={
                          activeSubMenu === "holidays" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo("holidays", "hr", "/hr/holidays", "Holiday")
                        }
                      >
                        <CalendarOff className="submenu-icon" />
                        <span>Holiday</span>
                      </li>
                    )}

                    {canView("travelAllowance") && (
                      <li
                        className={
                          activeSubMenu === "allowance" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo("allowance", "hr", "/hr/allowance", "Allowance")
                        }
                      >
                        <Wallet className="submenu-icon" />
                        <span>Allowance</span>
                      </li>
                    )}

                    {canView("leaveManagement") && (
                      <li
                        className={
                          activeSubMenu === "leave-management"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "leave-management",
                            "hr",
                            "/hr/leave-management",
                            "Leave Management",
                          )
                        }
                      >
                        <Leaf className="submenu-icon" />
                        <span>Leave</span>
                      </li>
                    )}

                    {canView("salaryManagement") && (
                      <li
                        className={
                          activeSubMenu === "salary-management"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "salary-management",
                            "hr",
                            "/hr/salary-management",
                            "Salary Management",
                          )
                        }
                      >
                        <Wallet className="submenu-icon" />
                        <span>Salary</span>
                      </li>
                    )}

                    {canView("employeeAdvance") && (
                      <li
                        className={
                          activeSubMenu === "employee-advance"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "employee-advance",
                            "hr",
                            "/hr/employee-advance",
                            "Employee Advance",
                          )
                        }
                      >
                        <CreditCard className="submenu-icon" />
                        <span>Advance</span>
                      </li>
                    )}

                    {canView("payslip") &&
                      (isHrAdmin ? (
                        <li
                          className={
                            activeSubMenu === "payslip-requests"
                              ? "submenu-active"
                              : ""
                          }
                          onClick={() =>
                            goTo(
                              "payslip-requests",
                              "hr",
                              "/hr/paysliprequest",
                              "Payslip",
                            )
                          }
                        >
                          <CreditCard className="submenu-icon" />
                          <span>Payslip</span>
                        </li>
                      ) : (
                        <li
                          className={
                            activeSubMenu === "employee-payslip"
                              ? "submenu-active"
                              : ""
                          }
                          onClick={() =>
                            goTo(
                              "employee-payslip",
                              "hr",
                              "/employeepayslip",
                              "My Payslip",
                            )
                          }
                        >
                          <CreditCard className="submenu-icon" />
                          <span>My Payslip</span>
                        </li>
                      ))}

                    {canView("idCard") && (
                      <li
                        className={
                          activeSubMenu === "id-card" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo("id-card", "hr", "/hr/id-card", "ID Card")
                        }
                      >
                        <IdCard className="submenu-icon" />
                        <span>ID Card</span>
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 3: LEADS */}
            {/* ══════════════════════════════════ */}

            {(canView("leads") ||
              canView("enquiryList") ||
              canView("newEnquiry") ||
              canView("followUps") ||
              canView("siteVisit") ||
              // canView("quotation") ||
              canView("future") ||
              canView("estimate") ||
              canView("siteDesigner")) && (
              <>
                <li
                  className={`menu-item ${
                    activeMainMenu === "leads" ? "menu-active" : ""
                  }`}
                  onClick={() => handleMainClick("leads", true)}
                  onMouseEnter={(e) => {
                    handleMouseEnter(e, "Leads Management");

                    handleSubmenuParentHover(e, "leads");
                  }}
                  onMouseLeave={() => {
                    handleMouseLeave();
                    handleSubmenuParentLeave();
                  }}
                >
                  <Briefcase />

                  {!collapsed && (
                    <>
                      <span>Leads</span>

                      <ChevronDown
                        className={`dropdown-arrow ${
                          isOpen("leads") ? "open" : ""
                        }`}
                      />
                    </>
                  )}
                </li>
                {isOpen("leads") && !collapsed && (
                  <ul className="submenu">
                    {(canView("leads") || canView("enquiryList")) && (
                      <li
                        className={
                          activeSubMenu === "enquiry-list"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "enquiry-list",
                            "leads",
                            "/leads/enquiry-list",
                            "Enquiry List",
                          )
                        }
                      >
                        <FileText className="submenu-icon" />
                        <span>Enquiry List</span>
                      </li>
                    )}

                    {(canView("leads") || canView("newEnquiry")) && (
                      <li
                        className={
                          activeSubMenu === "new-enquiry"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "new-enquiry",
                            "leads",
                            "/leads/new-enquiry",
                            "New Enquiry",
                          )
                        }
                      >
                        <FileQuestion className="submenu-icon" />
                        <span>New Enquiry</span>
                      </li>
                    )}

                    {(canView("leads") || canView("followUps")) && (
                      <li
                        className={
                          activeSubMenu === "follow-up" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "follow-up",
                            "leads",
                            "/leads/follow-up",
                            "Follow Ups",
                          )
                        }
                      >
                        <PhoneCall className="submenu-icon" />
                        <span>Follow Ups</span>
                      </li>
                    )}

                    {(canView("leads") || canView("siteVisit")) && (
                      <li
                        className={
                          activeSubMenu === "site-visit" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "site-visit",
                            "leads",
                            "/leads/site-visit",
                            "Site Visit",
                          )
                        }
                      >
                        <MapPin className="submenu-icon" />
                        <span>Site Visit</span>
                      </li>
                    )}

                    {/* {(canView("leads") || canView("quotation")) && (
                      <li
                        className={
                          activeSubMenu === "quotation-sent"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "quotation-sent",
                            "leads",
                            "/leads/quotation-sent",
                            "Quotation Sent",
                          )
                        }
                      >
                        <Quotation className="submenu-icon" />
                        <span>Quotation Sent</span>
                      </li>
                    )} */}

                    {(canView("leads") || canView("future")) && (
                      <li
                        className={
                          activeSubMenu === "future" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "future",
                            "leads",
                            "/leads/future",
                            "Future Follow Up",
                          )
                        }
                      >
                        <ClockIcon className="submenu-icon" />
                        <span>Future Follow</span>
                      </li>
                    )}

                    {(canView("leads") || canView("estimate")) && (
                      <li
                        className={
                          activeSubMenu === "estimate" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "estimate",
                            "leads",
                            "/leads/estimate",
                            "Estimate",
                          )
                        }
                      >
                        <EstimateIcon className="submenu-icon" />
                        <span>Estimate</span>
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE: SITE DESIGNER */}
            {(canView("leads") || canView("siteDesigner")) && (
              <li
                className={`menu-item ${activeMainMenu === "site-designer" ? "menu-active" : ""}`}
                onClick={() =>
                  handleMainClick(
                    "site-designer",
                    false,
                    "/leads/site-designer",
                    "Site Designer",
                  )
                }
                onMouseEnter={(e) => handleMouseEnter(e, "Site Designer")}
                onMouseLeave={handleMouseLeave}
              >
                <SiteDesignerIcon />
                {!collapsed && <span>Site Designer</span>}
              </li>
            )}

            {/* MODULE 6: CLIENTS */}
            {/* ════════════════════════════════════════════════════ */}

            {canView("clients") && (
              <li
                className={`menu-item ${
                  activeMainMenu === "clients" ? "menu-active" : ""
                }`}
                onClick={() =>
                  handleMainClick("clients", false, "/clients", "Clients")
                }
                onMouseEnter={(e) => handleMouseEnter(e, "Clients")}
                onMouseLeave={handleMouseLeave}
              >
                <Users2 />

                {!collapsed && <span>Clients</span>}
              </li>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 8: PROJECTS */}
            {/* ════════════════════════════════════════════════════ */}

            {canView("projects") && (
              <li
                className={`menu-item ${
                  activeMainMenu === "projects" ? "menu-active" : ""
                }`}
                onClick={() =>
                  handleMainClick("projects", false, "/projects", "Projects")
                }
                onMouseEnter={(e) => handleMouseEnter(e, "Projects")}
                onMouseLeave={handleMouseLeave}
              >
                <FolderKanban />

                {!collapsed && <span>Projects</span>}
              </li>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 9: CRAFTSMEN */}
            {/* ════════════════════════════════════════════════════ */}

            {(canView("carpenterMaster") ||
              canView("jobAssigned") ||
              canView("workCompletion") ||
              canView("carpenteradvance") ||
              canView("agents")) && (
              <>
                <li
                  className={`menu-item ${
                    activeMainMenu === "Craftsmen" ? "menu-active" : ""
                  }`}
                  onClick={() => handleMainClick("Craftsmen", true)}
                  onMouseEnter={(e) => {
                    handleMouseEnter(e, "Craftsmen");
                    handleSubmenuParentHover(e, "Craftsmen");
                  }}
                  onMouseLeave={() => {
                    handleMouseLeave();
                    handleSubmenuParentLeave();
                  }}
                >
                  <Hammer />

                  {!collapsed && (
                    <>
                      <span>Carpenter</span>
                      <ChevronDown
                        className={`dropdown-arrow ${
                          isOpen("Craftsmen") ? "open" : ""
                        }`}
                      />
                    </>
                  )}
                </li>

                {isOpen("Craftsmen") && !collapsed && (
                  <ul className="submenu">
                    {canView("carpenterMaster") && (
                      <li
                        className={
                          activeSubMenu === "Craftsmen-master"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "Craftsmen-master",
                            "Craftsmen",
                            "/craftsmen/craftsmen-master",
                            "Carpenter Master",
                          )
                        }
                      >
                        <CarpenterIcon className="submenu-icon" />
                        <span>Carpenter</span>
                      </li>
                    )}

                    {canView("jobAssigned") && (
                      <li
                        className={
                          activeSubMenu === "job-assigned"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "job-assigned",
                            "Craftsmen",
                            "/craftsmen/job-assigned",
                            "Job Assigned",
                          )
                        }
                      >
                        <Wrench className="submenu-icon" />
                        <span>Job Assigned</span>
                      </li>
                    )}

                    {canView("workCompletion") && (
                      <li
                        className={
                          activeSubMenu === "work-completion"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "work-completion",
                            "Craftsmen",
                            "/craftsmen/work-completion",
                            "Work Completion",
                          )
                        }
                      >
                        <CheckCircle className="submenu-icon" />
                        <span>Work Done</span>
                      </li>
                    )}

                    {canView("carpenteradvance") && (
                      <li
                        className={
                          activeSubMenu === "carpenteradvance"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "carpenteradvance",
                            "Craftsmen",
                            "/craftsmen/carpenteradvance",
                            "Carpenter Advance",
                          )
                        }
                      >
                        <CreditCard className="submenu-icon" />
                        <span>Advance</span>
                      </li>
                    )}

                    {canView("agents") && (
                      <li
                        className={
                          activeSubMenu === "agents" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "agents",
                            "Craftsmen",
                            "/craftsmen/agents",
                            "Agents",
                          )
                        }
                      >
                        <Users className="submenu-icon" />
                        <span>Team</span>
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 10: INVENTORY */}
            {/* ════════════════════════════════════════════════════ */}

            {(canView("catalog") ||
              canView("product") ||
              canView("purchase") ||
              canView("stock") ||
              canView("damage") ||
              canView("OrderDetail")) && (
              <>
                <li
                  className={`menu-item ${
                    activeMainMenu === "inventory" ? "menu-active" : ""
                  }`}
                  onClick={() => handleMainClick("inventory", true)}
                  onMouseEnter={(e) => {
                    handleMouseEnter(e, "Inventory");
                    handleSubmenuParentHover(e, "inventory");
                  }}
                  onMouseLeave={() => {
                    handleMouseLeave();
                    handleSubmenuParentLeave();
                  }}
                >
                  <Boxes />

                  {!collapsed && (
                    <>
                      <span>Inventory</span>
                      <ChevronDown
                        className={`dropdown-arrow ${
                          isOpen("inventory") ? "open" : ""
                        }`}
                      />
                    </>
                  )}
                </li>

                {isOpen("inventory") && !collapsed && (
                  <ul className="submenu">
                    {canView("catalog") && (
                      <li
                        className={
                          activeSubMenu === "catalog" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo("catalog", "inventory", "/catalog", "Catalog")
                        }
                      >
                        <FolderOpen className="submenu-icon" />
                        <span>Catalog</span>
                      </li>
                    )}

                    {canView("product") && (
                      <li
                        className={
                          activeSubMenu === "product" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "product",
                            "inventory",
                            "/inventory/product",
                            "Product",
                          )
                        }
                      >
                        <Package className="submenu-icon" />
                        <span>Product</span>
                      </li>
                    )}

                    {canView("purchase") && (
                      <>
                        <li
                          className={
                            activeSubMenu === "purchase" ? "submenu-active" : ""
                          }
                          onClick={() =>
                            goTo(
                              "purchase",
                              "inventory",
                              "/inventory/purchase",
                              "Purchase",
                            )
                          }
                        >
                          <ShoppingBag className="submenu-icon" />
                          <span>Purchase</span>
                        </li>

                        <li
                          className={
                            activeSubMenu === "purchase-return"
                              ? "submenu-active"
                              : ""
                          }
                          onClick={() =>
                            goTo(
                              "purchase-return",
                              "inventory",
                              "/inventory/purchase-return",
                              "Purchase Return",
                            )
                          }
                        >
                          <RotateCcw className="submenu-icon" />
                          <span>Pur Return</span>
                        </li>
                      </>
                    )}

                    {canView("stock") && (
                      <li
                        className={
                          activeSubMenu === "stock" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "stock",
                            "inventory",
                            "/inventory/stock",
                            "Stock",
                          )
                        }
                      >
                        <Archive className="submenu-icon" />
                        <span>Stock</span>
                      </li>
                    )}

                    {canView("stock") && (
                      <li
                        className={
                          activeSubMenu === "stock-report"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "stock-report",
                            "inventory",
                            "/inventory/stock-report",
                            "Stock Report",
                          )
                        }
                      >
                        <BarChart3 className="submenu-icon" />
                        <span>Stock Report</span>
                      </li>
                    )}

                    {canView("damage") && (
                      <li
                        className={
                          activeSubMenu === "damage" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "damage",
                            "inventory",
                            "/inventory/damage",
                            "Damage",
                          )
                        }
                      >
                        <AlertTriangle className="submenu-icon" />
                        <span>Damage</span>
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 11: WAREHOUSE */}
            {/* ════════════════════════════════════════════════════ */}

            {canView("warehouse") && (
              <li
                className={`menu-item ${
                  activeMainMenu === "warehouse" ? "menu-active" : ""
                }`}
                onClick={() =>
                  handleMainClick("warehouse", false, "/warehouse", "Warehouse")
                }
                onMouseEnter={(e) => handleMouseEnter(e, "Warehouse")}
                onMouseLeave={handleMouseLeave}
              >
                <Warehouse />

                {!collapsed && <span>Warehouse</span>}
              </li>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 12: TRANSPORT */}
            {/* ════════════════════════════════════════════════════ */}

            {(canView("transport") ||
              canView("vehicles") ||
              canView("drivers") ||
              canView("shipmentHistory") ||
              canView("transportSalary")) && (
              <>
                <li
                  className={`menu-item ${
                    activeMainMenu === "transport" ? "menu-active" : ""
                  }`}
                  onClick={() => handleMainClick("transport", true)}
                  onMouseEnter={(e) => {
                    handleMouseEnter(e, "Transport");
                    handleSubmenuParentHover(e, "transport");
                  }}
                  onMouseLeave={() => {
                    handleMouseLeave();
                    handleSubmenuParentLeave();
                  }}
                >
                  <Truck />

                  {!collapsed && (
                    <>
                      <span>Transport</span>
                      <ChevronDown
                        className={`dropdown-arrow ${
                          isOpen("transport") ? "open" : ""
                        }`}
                      />
                    </>
                  )}
                </li>

                {isOpen("transport") && !collapsed && (
                  <ul className="submenu">
                    {(canView("transport") || canView("vehicles")) && (
                      <li
                        className={
                          activeSubMenu === "vehicles" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "vehicles",
                            "transport",
                            "/transport/vehicles",
                            "Vehicles",
                          )
                        }
                      >
                        <TruckIcon className="submenu-icon" />
                        <span>Vehicles</span>
                      </li>
                    )}

                    {(canView("transport") || canView("drivers")) && (
                      <li
                        className={
                          activeSubMenu === "drivers" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "drivers",
                            "transport",
                            "/transport/drivers",
                            "Drivers",
                          )
                        }
                      >
                        <UserCircle className="submenu-icon" />
                        <span>Drivers</span>
                      </li>
                    )}

                    {(canView("transport") || canView("shipmentHistory")) && (
                      <li
                        className={
                          activeSubMenu === "shipment-history"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "shipment-history",
                            "transport",
                            "/transport/shipments/history",
                            "Shipment History",
                          )
                        }
                      >
                        <History className="submenu-icon" />
                        <span>History</span>
                      </li>
                    )}

                    {(canView("transport") || canView("transportSalary")) && (
                      <li
                        className={
                          activeSubMenu === "transport-salary"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "transport-salary",
                            "transport",
                            "/transport/drivers/salary",
                            "Transport Salary",
                          )
                        }
                      >
                        <Wallet className="submenu-icon" />
                        <span>Salary</span>
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}
            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 7: ORDERS (was Dealer & Distributor — Distributor/Trader removed) */}
            {/* ════════════════════════════════════════════════════ */}

            {(canView("dealerOrders") || canView("dealer")) && (
              <li
                className={`menu-item ${
                  activeMainMenu === "distributor-orders" ? "menu-active" : ""
                }`}
                onClick={() =>
                  handleMainClick(
                    "distributor-orders",
                    false,
                    "/distributor-orders",
                    "Orders",
                  )
                }
                onMouseEnter={(e) => handleMouseEnter(e, "Orders")}
                onMouseLeave={handleMouseLeave}
              >
                <Target />

                {!collapsed && <span>Orders</span>}
              </li>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 13: BILLING */}
            {/* ════════════════════════════════════════════════════ */}

            {canView("Billing") && (
              <>
                <li
                  className={`menu-item ${
                    activeMainMenu === "billing" ? "menu-active" : ""
                  }`}
                  onClick={() => handleMainClick("billing", true)}
                  onMouseEnter={(e) => {
                    handleMouseEnter(e, "Billing");
                    handleSubmenuParentHover(e, "billing");
                  }}
                  onMouseLeave={() => {
                    handleMouseLeave();
                    handleSubmenuParentLeave();
                  }}
                >
                  <IndianRupee />

                  {!collapsed && (
                    <>
                      <span>Billing</span>
                      <ChevronDown
                        className={`dropdown-arrow ${
                          isOpen("billing") ? "open" : ""
                        }`}
                      />
                    </>
                  )}
                </li>

                {isOpen("billing") && !collapsed && (
                  <ul className="submenu">
                    <li
                      className={
                        activeSubMenu === "billing-list" ? "submenu-active" : ""
                      }
                      onClick={() =>
                        goTo(
                          "billing-list",
                          "billing",
                          "/billing",
                          "Billing List",
                        )
                      }
                    >
                      <Receipt className="submenu-icon" />
                      <span>Billing List</span>
                    </li>

                    <li
                      className={
                        activeSubMenu === "billing-add" ? "submenu-active" : ""
                      }
                      onClick={() =>
                        goTo(
                          "billing-add",
                          "billing",
                          "/billing/add",
                          "Add Billing",
                        )
                      }
                    >
                      <Plus className="submenu-icon" />
                      <span>Add Billing</span>
                    </li>

                    <li
                      className={
                        activeSubMenu === "billing-pending"
                          ? "submenu-active"
                          : ""
                      }
                      onClick={() =>
                        goTo(
                          "billing-pending",
                          "billing",
                          "/billing/pending-billing",
                          "Pending Billing",
                        )
                      }
                    >
                      <ClockArrowUp className="submenu-icon" />
                      <span>Pending Billing</span>
                    </li>

                    <li
                      className={
                        activeSubMenu === "balance-sheet"
                          ? "submenu-active"
                          : ""
                      }
                      onClick={() =>
                        goTo(
                          "balance-sheet",
                          "billing",
                          "/billing/balance-sheet",
                          "Balance Sheet",
                        )
                      }
                    >
                      <BarChart3 className="submenu-icon" />
                      <span>Balance Sheet</span>
                    </li>

                    <li
                      className={
                        activeSubMenu === "sales-ledger" ? "submenu-active" : ""
                      }
                      onClick={() =>
                        goTo(
                          "sales-ledger",
                          "billing",
                          "/billing/sales-ledger",
                          "Ledger",
                        )
                      }
                    >
                      <FileSpreadsheet className="submenu-icon" />
                      <span>Ledger</span>
                    </li>

                    <li
                      className={
                        activeSubMenu === "client-sales-history"
                          ? "submenu-active"
                          : ""
                      }
                      onClick={() =>
                        goTo(
                          "client-sales-history",
                          "billing",
                          "/billing/client-sales-history",
                          "Client Sales History",
                        )
                      }
                    >
                      <WalletCards className="submenu-icon" />
                      <span>Recipient</span>
                    </li>

                    <li
                      className={
                        activeSubMenu === "sale-return" ? "submenu-active" : ""
                      }
                      onClick={() =>
                        goTo(
                          "sale-return",
                          "billing",
                          "/billing/sale-return",
                          "Sale Return",
                        )
                      }
                    >
                      <RotateCcw className="submenu-icon" />
                      <span>Sale Return</span>
                    </li>
                  </ul>
                )}
              </>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 16: EXPENSES */}
            {/* ════════════════════════════════════════════════════ */}

            {canView("expenses") && (
              <li
                className={`menu-item ${
                  activeMainMenu === "expenses" ? "menu-active" : ""
                }`}
                onClick={() =>
                  handleMainClick("expenses", false, "/expenses", "Expenses")
                }
                onMouseEnter={(e) => handleMouseEnter(e, "Expenses")}
                onMouseLeave={handleMouseLeave}
              >
                <Receipt />

                {!collapsed && <span>Expenses</span>}
              </li>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 5: TASK MANAGEMENT */}
            {/* ════════════════════════════════════════════════════ */}

            {canView("taskManagement") && (
              <li
                className={`menu-item ${
                  activeMainMenu === "task-module" ? "menu-active" : ""
                }`}
                onClick={() =>
                  handleMainClick(
                    "task-module",
                    false,
                    "/hr/task-management",
                    "Task Management",
                  )
                }
                onMouseEnter={(e) => handleMouseEnter(e, "Task Management")}
                onMouseLeave={handleMouseLeave}
              >
                <ListTodo />

                {!collapsed && <span>Task Management</span>}
              </li>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 15: ASSETS */}
            {/* ════════════════════════════════════════════════════ */}

            {canView("assets") && (
              <li
                className={`menu-item ${
                  activeMainMenu === "assets" ? "menu-active" : ""
                }`}
                onClick={() =>
                  handleMainClick("assets", false, "/assets", "Assets")
                }
                onMouseEnter={(e) => handleMouseEnter(e, "Assets")}
                onMouseLeave={handleMouseLeave}
              >
                <Landmark />

                {!collapsed && <span>Assets</span>}
              </li>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 14: DIGITAL MARKETING */}
            {/* ════════════════════════════════════════════════════ */}

            {canView("digitalmarketing") && (
              <li
                className={`menu-item ${
                  activeMainMenu === "digitalmarketing" ? "menu-active" : ""
                }`}
                onClick={() =>
                  handleMainClick(
                    "digitalmarketing",
                    false,
                    "/digitalmarketing",
                    "Digital Marketing",
                  )
                }
                onMouseEnter={(e) => handleMouseEnter(e, "Digital Marketing")}
                onMouseLeave={handleMouseLeave}
              >
                <RiAdvertisementLine />

                {!collapsed && <span>Digital Marketing</span>}
              </li>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 19: REPORTS */}
            {/* ════════════════════════════════════════════════════ */}

            {(canView("reportsSales") ||
              canView("reportsPurchase") ||
              canView("reportsStock") ||
              canView("reportsProducts") ||
              canView("reportsProject") ||
              canView("reportsLead") ||
              canView("reportsDealer") ||
              canView("reportsEmployee") ||
              canView("reportsAttendance") ||
              canView("reportsCarpenter") ||
              canView("reportsExpense") ||
              canView("reportsDamage") ||
              canView("reportsShipment") ||
              canView("reportsSalary") ||
              canView("reportsClients") ||
              canView("reportsTasks") ||
              canView("reportsPurchaseReturn") ||
              canView("reportsSaleReturn") ||
              canView("reportsLeave") ||
              canView("reportsAllowance") ||
              canView("reportsEmployeeAdvance") ||
              canView("reportsJobAssigned") ||
              canView("reportsWorkCompletion") ||
              canView("reportsVehicles") ||
              canView("reportsDrivers") ||
              canView("reportsDriverSalary") ||
              canView("reportsAssets") ||
              canView("reportsBalanceSheet") ||
              canView("reportsSalesLedger")) && (
              <>
                <li
                  className={`menu-item ${
                    activeMainMenu === "reports" ? "menu-active" : ""
                  }`}
                  onClick={() =>
                    handleMainClick("reports", false, "/reports?tab=Overview", "Reports")
                  }
                  onMouseEnter={(e) => handleMouseEnter(e, "Reports")}
                  onMouseLeave={handleMouseLeave}
                >
                  <BarChart3 />
                  {!collapsed && <span>Reports</span>}
                </li>
              </>
            )}

            {/* ════════════════════════════════════════════════════ */}
          </ul>
        </div>
      </aside>

      {/* ─────────────────────────────────────────
          COLLAPSED SIDEBAR TOOLTIP
      ───────────────────────────────────────── */}

      {hoveredTooltip && collapsed && !hoveredSubmenuParent && (
        <div
          className="sidebar-tooltip"
          style={{
            position: "fixed",
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: "translateY(-50%)",
            zIndex: 4000,
          }}
        >
          {hoveredTooltip}
        </div>
      )}

      {/* ─────────────────────────────────────────
          COLLAPSED SUBMENU TOOLTIP
      ───────────────────────────────────────── */}

      {hoveredSubmenuParent &&
        collapsed &&
        submenuData[hoveredSubmenuParent] && (
          <div
            className="submenu-tooltip-container"
            style={{
              position: "fixed",
              top: `${submenuTooltipPosition.top}px`,
              left: `${submenuTooltipPosition.left}px`,
              zIndex: 4000,
            }}
            onMouseEnter={handleSubmenuTooltipEnter}
            onMouseLeave={handleSubmenuTooltipLeave}
          >
            {submenuData[hoveredSubmenuParent]
              .filter((item) => item.canView)
              .map((item) => {
                const IconComponent = item.icon;

                return (
                  <div
                    key={item.key}
                    className={`submenu-tooltip-item ${
                      activeSubMenu === item.key ? "submenu-tooltip-active" : ""
                    }`}
                    onClick={() => {
                      setActiveSubMenu(item.key);
                      setActiveMainMenu(hoveredSubmenuParent);
                      navigate(item.path);
                      setTitle(item.label);
                      setHoveredSubmenuParent(null);
                    }}
                  >
                    {isReactIcon(IconComponent) ? (
                      <RIcon
                        icon={IconComponent}
                        size={18}
                        className="submenu-tooltip-icon"
                      />
                    ) : (
                      <IconComponent
                        size={18}
                        className="submenu-tooltip-icon"
                      />
                    )}

                    <span>{item.label}</span>
                  </div>
                );
              })}
          </div>
        )}
    </>
  );
};

export default Sidebar;
